import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotepad } from '../contexts/NotepadContext';

// Floating "Clip" button that appears when the attorney selects text anywhere
// in the case workspace (document viewer text/transcript, an Arc answer, the
// inventory, …). Clicking it captures the selection as a notepad clip.
//
// Document context is read from the nearest ancestor carrying
// ``data-np-doc-id`` (set on the DocumentViewer root) and an optional
// ``data-np-page``. With a doc context the clip is a `highlight`
// (anchor.kind=char_range); without one it's a freeform `note` holding the
// quote. Pixel-precise bbox anchoring + on-document overlay re-render arrive
// in the persistent-overlay phase — this captures the text + page now, which
// is enough for the deep-link `q=` highlight on reopen.

const MIN_SELECTION_LEN = 3;

function closestAttr(node, attr) {
  let el = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  while (el) {
    if (el.hasAttribute && el.hasAttribute(attr)) return el.getAttribute(attr);
    el = el.parentElement;
  }
  return null;
}

export default function SelectionClipper() {
  const notepad = useNotepad();
  const [bubble, setBubble] = useState(null); // { x, y, text, documentId, page }
  const bubbleRef = useRef(null);

  const clear = useCallback(() => setBubble(null), []);

  useEffect(() => {
    if (!notepad) return undefined;

    function onMouseUp(e) {
      // Ignore clicks that land on our own bubble.
      if (bubbleRef.current && bubbleRef.current.contains(e.target)) return;
      // Defer so the selection is finalized.
      setTimeout(() => {
        const sel = window.getSelection();
        const text = sel ? sel.toString().trim() : '';
        if (!sel || sel.isCollapsed || text.length < MIN_SELECTION_LEN) {
          setBubble(null);
          return;
        }
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          setBubble(null);
          return;
        }
        const anchorNode = sel.anchorNode;
        // Don't hijack selections made inside form fields (e.g. the note
        // editor or quick-add box) — that's text editing, not clipping.
        const anchorEl = anchorNode?.nodeType === Node.ELEMENT_NODE
          ? anchorNode : anchorNode?.parentElement;
        if (anchorEl && anchorEl.closest('input, textarea, [contenteditable="true"]')) {
          setBubble(null);
          return;
        }
        const documentId = closestAttr(anchorNode, 'data-np-doc-id');
        const pageRaw = closestAttr(anchorNode, 'data-np-page');
        const page = pageRaw ? parseInt(pageRaw, 10) : null;
        // Selections inside an Arc answer carry conversation context so the
        // clip links back to the chat instead of becoming an anchorless note.
        const conversationId = closestAttr(anchorNode, 'data-np-chat-conversation');
        const messageId = closestAttr(anchorNode, 'data-np-chat-message');
        setBubble({
          x: rect.left + rect.width / 2,
          y: rect.top - 8,
          text,
          documentId,
          page: Number.isFinite(page) ? page : null,
          conversationId,
          messageId,
        });
      }, 0);
    }

    function onScroll() { setBubble(null); }

    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [notepad]);

  if (!notepad || !bubble) return null;

  const onClip = async () => {
    const { text, documentId, page, conversationId, messageId } = bubble;
    if (documentId) {
      await notepad.captureClip({
        kind: 'highlight',
        document_id: documentId,
        anchor: page != null
          ? { kind: 'char_range', page }
          : { kind: 'char_range' },
        quote: text,
      });
    } else if (conversationId) {
      // Selection from an Arc answer → linkable chat clip.
      await notepad.captureClip({
        kind: 'chat',
        quote: text,
        conversation_id: conversationId,
        message_id: messageId || undefined,
      });
    } else {
      await notepad.captureClip({ kind: 'note', quote: text });
    }
    window.getSelection()?.removeAllRanges();
    setBubble(null);
  };

  // Clamp horizontally so the bubble stays on-screen.
  const left = Math.min(Math.max(bubble.x, 70), window.innerWidth - 70);

  return (
    <button
      ref={bubbleRef}
      className="np-clip-bubble"
      style={{ left, top: Math.max(bubble.y, 8) }}
      // Prevent the mousedown from collapsing the selection before onClick.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClip}
    >
      📌 Clip
    </button>
  );
}
